import React, { useMemo, useRef, useState } from 'react';

import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectOrgData } from 'selectors/orgData';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import { ChevronDown, Goals } from 'assets/assetsV1/iconComponents';
import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { OBJECTIVE_ACCESS_PERMISSIONS } from 'constants/ProjectConstants';
import {
  sendClickedOnAddNewButton,
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendOpenedObjectivePanelEvent,
  sendOpenedKRPanelEvent,
  sendPageViewEvent,
} from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';

import { OKRMenuOptions } from './OKRMenuOptions';

export const AddNewGoalObjectiveButton = ({ onAddKRClick = () => {}, onAddObjectiveClick = () => {} }) => {
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const { objectiveCreationAccess } = useSelector(selectOrgData);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const anchorRef = useRef();
  const page = useViewPageName();

  const showObjectiveOption = useMemo(() => {
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[0].value) {
      return checkUserIsGoalAdmin();
    }
    if (objectiveCreationAccess === OBJECTIVE_ACCESS_PERMISSIONS[1].value) {
      return checkUserIsGoalAdmin() || loggedInEmployee?.isHRISManager;
    }

    return false;
  }, [objectiveCreationAccess, loggedInEmployee]);

  return (
    <AddAccessControlledNewKRObjectiveButton
      onAddKRClick={() => {
        sendClickedOnAddNewButton({
          eventProperties: {
            type: 'KR',
            page,
          },
        });
        sendClickedOnCreateKRevent({
          eventProperties: { source: 'Add New Button', page },
        });
        sendOpenedKRPanelEvent({
          eventProperties: {
            source: 'Add New Button',
            page,
            type: 'Add KR Panel',
          },
        });
        sendPageViewEvent({
          eventProperties: {
            source: 'Add New Button',
            page: 'Add KR Panel',
            type: 'Panel',
          },
        });
        onAddKRClick();
      }}
      onAddObjectiveClick={() => {
        sendClickedOnAddNewButton({
          eventProperties: {
            type: 'Obj',
            page,
          },
        });
        sendClickedOnCreateObjectiveEvent({
          eventProperties: { source: 'Add New Button', page },
        });
        sendOpenedObjectivePanelEvent({
          eventProperties: {
            source: 'Add New Button',
            page,
            type: 'Add Objective Panel',
          },
        });
        sendPageViewEvent({
          eventProperties: {
            source: 'Add New Button',
            page: 'Add Objective Panel',
            type: 'Panel',
          },
        });
        onAddObjectiveClick();
      }}
    />
  );
};

const AddAccessControlledNewKRObjectiveButton = ({ onAddObjectiveClick, onAddKRClick }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const anchorRef = useRef();

  /* Access Control code starts here */
  const {
    canCreateObjectiveAtRootLevel,

    canCreateObjectiveUnderKeyResult,
    canCreateObjectiveUnderObjective,
    canCreateKeyResultUnderKeyResult,
    canCreateKeyResultUnderObjective,
    canCreateKeyResultAtRootLevel,
  } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateObjective =
    canCreateObjectiveAtRootLevel || canCreateObjectiveUnderObjective || canCreateObjectiveUnderKeyResult;

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  const canCreateBothObjectiveAndKeyResult = canCreateObjective && canCreateKeyResult;
  const cannotCreateAnyOKR = !canCreateObjective && !canCreateKeyResult;

  /* Access Control code ends here */

  if (cannotCreateAnyOKR) return null;

  return (
    <>
      {canCreateBothObjectiveAndKeyResult ? (
        <span
          ref={anchorRef}
          onClick={(ev) => {
            ev.stopPropagation();
            setMenuIsOpen((prev) => !prev);
          }}
        >
          <Button variant="rowButtonFilled" StartIcon={Goals} EndIcon={ChevronDown} pr="2px">
            Add New
          </Button>
          <OKRMenuOptions
            addKR={onAddKRClick}
            addObjective={onAddObjectiveClick}
            anchorEl={anchorRef}
            isOpen={menuIsOpen}
            setIsOpen={setMenuIsOpen}
          />
        </span>
      ) : canCreateObjective ? (
        <Button StartIcon={Objective} onClick={onAddObjectiveClick} ml="auto" pr="2px">
          Add New {objectivesName}
        </Button>
      ) : (
        <Button StartIcon={Goals} onClick={onAddKRClick} ml="auto" pr="2px">
          Add New {keyResultsName}
        </Button>
      )}
    </>
  );
};
